import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatDate, formatCurrency } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  HandCoins,
  Plus,
  Search,
  ChevronLeft,
  ChevronRight,
  Loader2,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface OtherIncome {
  id: string;
  businessProfileId: string;
  businessProfileName: string;
  description: string;
  amount: number;
  incomeDate: string;
  incomeType: string;
  paymentMethod: string | null;
  referenceNumber: string | null;
  notes: string | null;
}

interface BusinessProfile {
  id: string;
  name: string;
}

const incomeTypeLabels: Record<string, string> = {
  freelance: 'Freelance',
  consulting: 'Consulting',
  interest: 'Interest',
  refund: 'Refund',
  grant: 'Grant',
  royalty: 'Royalty',
  rental: 'Rental',
  commission: 'Commission',
  tip: 'Tip',
  reimbursement: 'Reimbursement',
  other: 'Other',
};

const incomeTypeColors: Record<string, 'default' | 'secondary' | 'success' | 'warning' | 'info'> = {
  freelance: 'info',
  consulting: 'info',
  interest: 'success',
  refund: 'warning',
  grant: 'success',
  royalty: 'default',
  rental: 'default',
  commission: 'info',
  tip: 'success',
  reimbursement: 'warning',
  other: 'secondary',
};

const methodLabels: Record<string, string> = {
  check: 'Check',
  ach: 'ACH',
  wire: 'Wire',
  credit_card: 'Credit Card',
  cash: 'Cash',
  zelle: 'Zelle',
  other: 'Other',
};

export function OtherIncomePage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [profileFilter, setProfileFilter] = useState('');
  const [page, setPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<OtherIncome | null>(null);

  const { data: profilesData } = useQuery({
    queryKey: ['businessProfiles'],
    queryFn: () => api.getBusinessProfiles(),
  });

  const { data, isLoading } = useQuery({
    queryKey: ['otherIncome', { page, search, incomeType: typeFilter, businessProfileId: profileFilter }],
    queryFn: () => api.getOtherIncome({
      page,
      limit: 20,
      search: search || undefined,
      incomeType: typeFilter || undefined,
      businessProfileId: profileFilter || undefined,
    }),
  });

  const profiles = (profilesData?.data || []) as BusinessProfile[];
  const items = (data?.data || []) as OtherIncome[];
  const pagination = data?.pagination;

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.createOtherIncome(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['otherIncome'] });
      queryClient.invalidateQueries({ queryKey: ['finance-dashboard-summary'] });
      setDialogOpen(false);
      toast({ title: 'Income entry created' });
    },
    onError: () => toast({ title: 'Failed to create income entry', variant: 'destructive' }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) => api.updateOtherIncome(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['otherIncome'] });
      queryClient.invalidateQueries({ queryKey: ['finance-dashboard-summary'] });
      setDialogOpen(false);
      setEditingItem(null);
      toast({ title: 'Income entry updated' });
    },
    onError: () => toast({ title: 'Failed to update income entry', variant: 'destructive' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteOtherIncome(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['otherIncome'] });
      queryClient.invalidateQueries({ queryKey: ['finance-dashboard-summary'] });
      toast({ title: 'Income entry deleted' });
    },
    onError: () => toast({ title: 'Failed to delete income entry', variant: 'destructive' }),
  });

  function openCreate() {
    setEditingItem(null);
    setDialogOpen(true);
  }

  function openEdit(item: OtherIncome) {
    setEditingItem(item);
    setDialogOpen(true);
  }

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const payload: Record<string, unknown> = {
      businessProfileId: formData.get('businessProfileId'),
      description: formData.get('description'),
      amount: parseFloat(formData.get('amount') as string),
      incomeDate: formData.get('incomeDate') || null,
      incomeType: formData.get('incomeType') || 'other',
      paymentMethod: formData.get('paymentMethod') || null,
      referenceNumber: formData.get('referenceNumber') || null,
      notes: formData.get('notes') || null,
    };

    if (editingItem) {
      updateMutation.mutate({ id: editingItem.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-end">
        <Button onClick={openCreate}>
          <Plus className="h-4 w-4 mr-2" />
          Add Income
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search income entries..."
            className="pl-9"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
        </div>
        <Select value={typeFilter || 'all'} onValueChange={(v) => { setTypeFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="All types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {Object.entries(incomeTypeLabels).map(([value, label]) => (
              <SelectItem key={value} value={value}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={profileFilter || 'all'} onValueChange={(v) => { setProfileFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="All profiles" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All profiles</SelectItem>
            {profiles.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <HandCoins className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-1">No other income entries</h3>
            <p className="text-muted-foreground mb-4">
              Add revenue received outside of formal invoices
            </p>
            <Button variant="outline" onClick={openCreate}>
              <Plus className="h-4 w-4 mr-2" />
              Add your first income entry
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left p-3 text-sm font-medium">Date</th>
                  <th className="text-left p-3 text-sm font-medium">Description</th>
                  <th className="text-left p-3 text-sm font-medium">Type</th>
                  <th className="text-left p-3 text-sm font-medium">Business Profile</th>
                  <th className="text-left p-3 text-sm font-medium">Payment Method</th>
                  <th className="text-right p-3 text-sm font-medium">Amount</th>
                  <th className="text-right p-3 text-sm font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id} className="border-b hover:bg-muted/30">
                    <td className="p-3 text-sm">{formatDate(item.incomeDate)}</td>
                    <td className="p-3 text-sm font-medium">
                      {item.description}
                      {item.referenceNumber && (
                        <span className="text-muted-foreground font-mono text-xs ml-2">
                          #{item.referenceNumber}
                        </span>
                      )}
                    </td>
                    <td className="p-3">
                      <Badge variant={incomeTypeColors[item.incomeType] || 'secondary'}>
                        {incomeTypeLabels[item.incomeType] || item.incomeType}
                      </Badge>
                    </td>
                    <td className="p-3 text-sm">{item.businessProfileName}</td>
                    <td className="p-3 text-sm">
                      {item.paymentMethod ? methodLabels[item.paymentMethod] || item.paymentMethod : '—'}
                    </td>
                    <td className="p-3 text-sm text-right font-medium text-green-600">
                      {formatCurrency(item.amount)}
                    </td>
                    <td className="p-3 text-right">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(item)}>Edit</Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive"
                        onClick={() => { if (confirm('Delete this income entry?')) deleteMutation.mutate(item.id); }}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {pagination.total} entr{pagination.total !== 1 ? 'ies' : 'y'}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm">Page {page} of {pagination.totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= pagination.totalPages} onClick={() => setPage(p => p + 1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent key={editingItem?.id || 'new'} className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit Income Entry' : 'Add Income Entry'}</DialogTitle>
            <DialogDescription>
              {editingItem
                ? 'Update the income entry details below.'
                : 'Record revenue received outside of formal invoices.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="businessProfileId">Business Profile *</Label>
                <select
                  name="businessProfileId"
                  id="businessProfileId"
                  required
                  defaultValue={editingItem?.businessProfileId || ''}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">Select profile</option>
                  {profiles.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <Label htmlFor="incomeType">Income Type</Label>
                <select
                  name="incomeType"
                  id="incomeType"
                  defaultValue={editingItem?.incomeType || 'other'}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  {Object.entries(incomeTypeLabels).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <Label htmlFor="description">Description *</Label>
              <Input name="description" id="description" required defaultValue={editingItem?.description || ''} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="amount">Amount *</Label>
                <Input
                  type="number"
                  name="amount"
                  id="amount"
                  step="0.01"
                  min="0.01"
                  required
                  defaultValue={editingItem?.amount || ''}
                />
              </div>
              <div>
                <Label htmlFor="incomeDate">Date</Label>
                <Input
                  type="date"
                  name="incomeDate"
                  id="incomeDate"
                  defaultValue={editingItem?.incomeDate ? editingItem.incomeDate.split('T')[0] : new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="paymentMethod">Payment Method</Label>
                <select
                  name="paymentMethod"
                  id="paymentMethod"
                  defaultValue={editingItem?.paymentMethod || ''}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">None</option>
                  {Object.entries(methodLabels).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="referenceNumber">Reference #</Label>
                <Input name="referenceNumber" id="referenceNumber" defaultValue={editingItem?.referenceNumber || ''} />
              </div>
            </div>
            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea name="notes" id="notes" rows={2} defaultValue={editingItem?.notes || ''} />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                {(createMutation.isPending || updateMutation.isPending) && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingItem ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
